import { devLog, devError } from '../utils/devLogger';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Form,
  Button,
  Table,
  Alert,
  Card,
  Row,
  Col,
  Badge,
  ButtonGroup,
  Spinner
} from 'react-bootstrap';
import {
  Timestamp,
  collection,
  getDocs,
  updateDoc,
  query,
  doc,
  where,
  orderBy,
  serverTimestamp,
  deleteDoc,
  writeBatch,
  limit,
  setDoc
} from 'firebase/firestore';
import { auth, db, functions, storage } from '../firebase';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { useAuth } from '../auth/AuthContext';
import attendanceService from '../services/attendanceService';
import facilitatorService from '../services/facilitatorService';
import NotificationService from '../services/notificationService';
import GoogleMeetAttendanceSync from './GoogleMeetAttendanceSync';
import AttendanceOverride from './AttendanceOverride';
import { isExternalFacilitatorRole, normalizeRole } from '../services/roleManagementService';
import '../styles/brand.css';
import { UI_TIMEOUTS } from '../constants/ui';

const chunkArray = (array, size) => {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
};

const ATTENDANCE_STATUS_META = {
  present: { label: 'Present', badge: 'success', button: 'success' },
  absent: { label: 'Absent', badge: 'danger', button: 'danger' },
  excused: { label: 'Excused', badge: 'warning', button: 'warning' },
  self_paced: { label: 'Self-paced', badge: 'info', button: 'info' },
  other: { label: 'Other', badge: 'primary', button: 'primary' },
  not_marked: { label: 'Not marked', badge: 'secondary', button: 'secondary' }
};

const getAttendanceStatusMeta = (status) =>
  ATTENDANCE_STATUS_META[status] || {
    label: String(status || 'not_marked').replace(/_/g, ' '),
    badge: 'secondary',
    button: 'secondary'
  };

const isActiveLearner = (learner) => {
  if (!learner) return false;
  if (learner.deleted || learner.deactivated) return false;
  if (learner.isActive === false) return false;
  const status = String(learner.status || '').toLowerCase();
  if (['inactive', 'dropoff', 'drop_off', 'drop off', 'dropped'].includes(status)) return false;
  return true;
};

const getDatesInRange = (startDate, endDate, allowedWeekdays = null) => {
  const dates = [];
  const cursor = new Date(startDate);
  const final = new Date(endDate);

  while (cursor <= final) {
    const day = cursor.getDay();
    if (!allowedWeekdays || allowedWeekdays.has(day)) {
      dates.push(cursor.toISOString().slice(0, 10));
    }
    cursor.setDate(cursor.getDate() + 1);
  }

  return dates;
};

  const AttendanceRegister = ({
  cohortId = null,
  facilitatorMode = false,
  modules: modulesProp = null,
  initialViewMode = 'calendar'
}) => {
  const { currentUser, userProfile } = useAuth();
  const externalFacilitatorLimited = isExternalFacilitatorRole(userProfile?.role);
  const allowSessionCreateAndMark = !externalFacilitatorLimited && ['admin', 'facilitator', 'qa'].includes(normalizeRole(userProfile?.role));
  const [modules, setModules] = useState(modulesProp || []);
  const [programs, setPrograms] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [learners, setLearners] = useState([]);
  const [attendanceHistory, setAttendanceHistory] = useState({});
  const [attendanceSignatures, setAttendanceSignatures] = useState({});
  const [loadingSignatures, setLoadingSignatures] = useState(false);
  const [attendanceDraft, setAttendanceDraft] = useState({});
  const [teamsSyncUsed, setTeamsSyncUsed] = useState(false);
  const [selectedModule, setSelectedModule] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedCohort, setSelectedCohort] = useState(cohortId || '');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
    end: new Date().toISOString().slice(0, 10)
  });
  const [viewMode, setViewMode] = useState(initialViewMode);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showTeamsSync, setShowTeamsSync] = useState(false);
  const [requestLearnerConfirmation, setRequestLearnerConfirmation] = useState(false);
  const [lastMarkedSession, setLastMarkedSession] = useState(null);
  const [lastMarkedPresentLearners, setLastMarkedPresentLearners] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [overrideSessions, setOverrideSessions] = useState([]);
  const [overrideSessionId, setOverrideSessionId] = useState('');
  const [loadingOverrideSessions, setLoadingOverrideSessions] = useState(false);
  const [overrideCohortFilter, setOverrideCohortFilter] = useState('');
  const [overrideRefreshKey, setOverrideRefreshKey] = useState(0);
  const [archivingSession, setArchivingSession] = useState(false);
  const [archiveError, setArchiveError] = useState('');
  const [manageSessions, setManageSessions] = useState([]);
  const [manageLoading, setManageLoading] = useState(false);
  const [manageError, setManageError] = useState('');
  const [manageCohortFilter, setManageCohortFilter] = useState('');
  const [showArchivedSessions, setShowArchivedSessions] = useState(false);
  const [manageActionLoading, setManageActionLoading] = useState(false);
  const [managePasswordError, setManagePasswordError] = useState('');
  const [registerUploads, setRegisterUploads] = useState({});
  const [loadingRegisterUploads, setLoadingRegisterUploads] = useState(false);
  const [calendarSearchTerm, setCalendarSearchTerm] = useState('');
  const [focusedCalendarLearnerId, setFocusedCalendarLearnerId] = useState('');
  const [registerSearchTerm, setRegisterSearchTerm] = useState('');
  const [bulkRegisterDate, setBulkRegisterDate] = useState('');
  const [bulkRegisterFile, setBulkRegisterFile] = useState(null);
  const [clearingMyRegisters, setClearingMyRegisters] = useState(false);
  const maxRegisterBytes = 10 * 1024 * 1024;
  const allowedRegisterExtensions = ['pdf', 'doc', 'docx'];
  const [selectedWeekdays, setSelectedWeekdays] = useState(new Set([0, 1, 2, 3, 4, 5, 6]));
  const hasInitialisedRef = useRef(false);
  const sanitizeFilePart = (value) =>
    String(value || '')
      .trim()
      .replace(/[<>:"/\\|?*]+/g, '_')
      .replace(/\s+/g, '_')
      .replace(/_+/g, '_')
      .slice(0, 80);
  const normalizeSessionDateValue = (value) => {
    if (!value) return null;
    const toLocalDateString = (date) => {
      if (!date || Number.isNaN(date.getTime())) return null;
      const year = date.getFullYear();
      const month = `${date.getMonth() + 1}`.padStart(2, '0');
      const day = `${date.getDate()}`.padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    if (typeof value === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
      return toLocalDateString(new Date(value));
    }
    if (value?.toDate) return toLocalDateString(value.toDate());
    if (value?.seconds) return toLocalDateString(new Date(value.seconds * 1000));
    return toLocalDateString(new Date(value));
  };

  useEffect(() => {
    const initialise = async () => {
      try {
        if (!hasInitialisedRef.current) {
          setLoading(true);
        }

        if (!modulesProp) {
          const modulesSnapshot = await getDocs(collection(db, 'modules'));
          const modulesList = modulesSnapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data()
          }));
          setModules(modulesList);
          if (!selectedModule && modulesList.length > 0) {
            setSelectedModule(modulesList[0].id);
          }
        }

        const programsSnapshot = await getDocs(collection(db, 'programs'));
        let programsList = programsSnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));

        const cohortsSnapshot = await getDocs(collection(db, 'cohorts'));
        let cohortsList = cohortsSnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));

        if (facilitatorMode && userProfile?.assignedPrograms?.length) {
          cohortsList = cohortsList.filter((cohort) => userProfile.assignedPrograms.includes(cohort.programId));
          programsList = programsList.filter((program) => userProfile.assignedPrograms.includes(program.id));
        }
        if (userProfile?.role?.startsWith('external') && userProfile?.assignedCohorts?.length) {
          cohortsList = cohortsList.filter((cohort) => userProfile.assignedCohorts.includes(cohort.id));
        }
        setPrograms(programsList);
        setCohorts(cohortsList);
        if (!selectedCohort && cohortsList.length > 0) {
          setSelectedCohort(cohortsList[0].id);
        }
        if (!selectedProgram && programsList.length > 0) {
          setSelectedProgram(programsList[0].id);
        }
        if (!overrideCohortFilter && cohortsList.length > 0) {
          setOverrideCohortFilter(facilitatorMode ? 'all' : cohortsList[0].id);
        }

        let learnerList = [];

        if (facilitatorMode && currentUser) {
          // For facilitators, load learners from assigned programs
          try {
            learnerList = await facilitatorService.getLearnersByAssignedPrograms(currentUser.uid);
          } catch (error) {
            devError('Error loading facilitator learners:', error);
          }
        } else {
          // For admin mode or non-facilitator, load all learners or by cohort
          let learnerQuery = query(collection(db, 'users'), where('role', '==', 'learner'));
          if (selectedCohort) {
            learnerQuery = query(
              collection(db, 'users'),
              where('role', '==', 'learner'),
              where('cohortId', '==', selectedCohort)
            );
          } else if (selectedProgram) {
            learnerQuery = query(
              collection(db, 'users'),
              where('role', '==', 'learner'),
              where('programId', '==', selectedProgram)
            );
          }

          const learnersSnapshot = await getDocs(learnerQuery);
          learnerList = learnersSnapshot.docs.map((docSnap) => ({
            uid: docSnap.id,
            ...docSnap.data()
          }));
        }

        if (facilitatorMode && learnerList.length > 0) {
          if (selectedProgram) {
            learnerList = learnerList.filter((learner) => learner.programId === selectedProgram);
          }
          if (selectedCohort) {
            learnerList = learnerList.filter((learner) => learner.cohortId === selectedCohort);
          }
        }

        const activeLearners = learnerList.filter((learner) => isActiveLearner(learner));
        setLearners(activeLearners);
        hasInitialisedRef.current = true;
        setError('');
        setSuccess('');
      } catch (initialiseError) {
        setError(initialiseError.message || 'Failed to load attendance register data.');
        // eslint-disable-next-line no-console
        devError('AttendanceRegister: failed to initialise', initialiseError);
      } finally {
        setLoading(false);
      }
    };

    initialise();
  }, [modulesProp, selectedCohort, facilitatorMode, userProfile?.assignedPrograms, selectedProgram]);

  useEffect(() => {
    const loadRegisterUploads = async () => {
      try {
        setLoadingRegisterUploads(true);
        const toLocalDateString = (date) => {
          if (!date || Number.isNaN(date.getTime())) return null;
          const year = date.getFullYear();
          const month = `${date.getMonth() + 1}`.padStart(2, '0');
          const day = `${date.getDate()}`.padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        const parseSessionDate = (value) => {
          if (!value) return null;
          if (typeof value === 'string') {
            // Keep canonical yyyy-mm-dd values untouched.
            if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
            const parsedStringDate = new Date(value);
            return toLocalDateString(parsedStringDate);
          }
          if (value?.toDate) {
            return toLocalDateString(value.toDate());
          }
          if (value?.seconds) {
            return toLocalDateString(new Date(value.seconds * 1000));
          }
          return toLocalDateString(new Date(value));
        };

        const learnerCohortById = learners.reduce((acc, learner) => {
          if (learner?.uid && learner?.cohortId) acc[learner.uid] = learner.cohortId;
          return acc;
        }, {});

        const uploadMap = {};

        const applyDocsToMap = (docs) => {
          docs.forEach((docSnap) => {
            const data = docSnap.data();
            const sessionDate = parseSessionDate(data?.sessionDate);
            if (!data?.learnerId || !sessionDate) return;
            const learnerCohort = learnerCohortById[data.learnerId];
            const docCohortId = data.cohortId || learnerCohort || null;
            uploadMap[`${data.learnerId}_${sessionDate}`] = {
              id: docSnap.id,
              ...data,
              sessionDate,
              cohortId: docCohortId
            };
          });
        };

        const snapshot = await getDocs(
          query(collection(db, 'attendance_register_uploads'), orderBy('createdAt', 'desc'), limit(3000))
        );
        applyDocsToMap(snapshot.docs);

        // Fallback 1: handles legacy docs where createdAt/sessionDate isn't stored as expected.
        if (Object.keys(uploadMap).length === 0) {
          const fallbackSnapshot = await getDocs(
            query(collection(db, 'attendance_register_uploads'), orderBy('createdAt', 'desc'), limit(1000))
          );
          applyDocsToMap(fallbackSnapshot.docs);
        }

        // Fallback 2: catch docs missing createdAt (excluded from orderBy queries).
        if (Object.keys(uploadMap).length === 0) {
          const fullSnapshot = await getDocs(collection(db, 'attendance_register_uploads'));
          applyDocsToMap(fullSnapshot.docs);
        }

        setRegisterUploads(uploadMap);
      } catch (err) {
        devError('AttendanceRegister: failed to load register uploads', err);
        setRegisterUploads({});
      } finally {
        setLoadingRegisterUploads(false);
      }
    };

    loadRegisterUploads();
  }, [selectedCohort, learners]);

  useEffect(() => {
    if (!selectedCohort) return;
    const cohort = cohorts.find((item) => item.id === selectedCohort);
    if (cohort?.programId && !selectedProgram) {
      setSelectedProgram(cohort.programId);
    }
  }, [selectedCohort, cohorts, selectedProgram]);

  useEffect(() => {
    const loadOverrideSessions = async () => {
      try {
        setLoadingOverrideSessions(true);

        if (!overrideCohortFilter) {
          setOverrideSessions([]);
          setOverrideSessionId('');
          return;
        }

        const normalizeEmail = (email) => String(email || '').toLowerCase().trim();
        const learnerEmailsForCohort = (cohortFilter) => {
          const list = cohortFilter === 'all'
            ? learners
            : learners.filter((learner) => learner.cohortId === cohortFilter);
          return new Set(list.map((learner) => normalizeEmail(learner.email)).filter(Boolean));
        };

        const filterCustomSessions = (sessions, cohortFilter) => {
          const emailSet = learnerEmailsForCohort(cohortFilter);
          return sessions.filter((session) => {
            const assignedEmails = Array.isArray(session.assignedUserEmails) ? session.assignedUserEmails : [];
            if (assignedEmails.length === 0) return false;
            return assignedEmails.some((email) => emailSet.has(normalizeEmail(email)));
          });
        };

        const baseSessions = [];

        if (overrideCohortFilter === 'all') {
          const cohortsToUse = cohorts.map((cohort) => cohort.id);
          for (let i = 0; i < cohortsToUse.length; i += 10) {
            const chunk = cohortsToUse.slice(i, i + 10);
            if (chunk.length === 0) continue;
            const sessionQuery = query(
              collection(db, 'sessions'),
              where('cohortId', 'in', chunk),
              orderBy('scheduledDate', 'desc')
            );
            const snapshot = await getDocs(sessionQuery);
            snapshot.docs.forEach((docSnap) => {
              baseSessions.push({
                id: docSnap.id,
                ...docSnap.data(),
                scheduledDate: docSnap.data().scheduledDate?.toDate?.() || null
              });
            });
          }
        } else {
          const result = await attendanceService.getSessionsForCohort(overrideCohortFilter);
          if (result.success) {
            baseSessions.push(...(result.sessions || []));
          }
        }

        const customSessions = [];
        try {
          const customQuery = query(
            collection(db, 'sessions'),
            where('cohortId', '==', null),
            orderBy('scheduledDate', 'desc')
          );
          const customSnapshot = await getDocs(customQuery);
          customSnapshot.docs.forEach((docSnap) => {
            customSessions.push({
              id: docSnap.id,
              ...docSnap.data(),
              scheduledDate: docSnap.data().scheduledDate?.toDate?.() || null
            });
          });
        } catch (customError) {
          // eslint-disable-next-line no-console
          devLog('AttendanceRegister: custom sessions query failed', customError);
        }

        const filteredCustom = filterCustomSessions(customSessions, overrideCohortFilter);
        const merged = [...baseSessions, ...filteredCustom].filter((session) => !session.archived);
        setOverrideSessions(merged);
      } catch (loadError) {
        // eslint-disable-next-line no-console
        devError('AttendanceRegister: failed to load sessions for override', loadError);
      } finally {
        setLoadingOverrideSessions(false);
      }
    };

    loadOverrideSessions();
  }, [overrideCohortFilter, cohorts.length, learners.length, overrideRefreshKey]);

  useEffect(() => {
    const loadManageSessions = async () => {
      if (!userProfile || !(userProfile.role === 'admin' || userProfile.role === 'qa')) return;
      try {
        setManageLoading(true);
        setManageError('');
        let sessionsQuery = query(collection(db, 'sessions'), orderBy('scheduledDate', 'desc'), limit(200));
        if (manageCohortFilter) {
          sessionsQuery = query(
            collection(db, 'sessions'),
            where('cohortId', '==', manageCohortFilter),
            orderBy('scheduledDate', 'desc'),
            limit(200)
          );
        }
        const snapshot = await getDocs(sessionsQuery);
        const sessions = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
          scheduledDate: docSnap.data().scheduledDate?.toDate?.() || null
        }));
        setManageSessions(sessions);
      } catch (err) {
        setManageError('Failed to load sessions.');
        // eslint-disable-next-line no-console
        devError('AttendanceRegister: failed to load manage sessions', err);
      } finally {
        setManageLoading(false);
      }
    };

    loadManageSessions();
  }, [userProfile, manageCohortFilter, overrideRefreshKey]);

  const requireAdminPassword = async () => {
    setManagePasswordError('');
    const email = currentUser?.email;
    if (!email) {
      setManagePasswordError('No email available for re-authentication.');
      return false;
    }
    const password = window.prompt('Enter your password to confirm this action:');
    if (!password) return false;
    try {
      const credential = EmailAuthProvider.credential(email, password);
      await reauthenticateWithCredential(auth.currentUser, credential);
      return true;
    } catch (err) {
      setManagePasswordError('Password verification failed.');
      return false;
    }
  };

  const handleArchiveToggle = async (session, shouldArchive) => {
    if (!session) return;
    try {
      setManageActionLoading(true);
      await updateDoc(doc(db, 'sessions', session.id), {
        archived: shouldArchive,
        archivedAt: shouldArchive ? serverTimestamp() : null,
        archivedBy: shouldArchive ? currentUser?.uid || null : null
      });
      setOverrideRefreshKey((prev) => prev + 1);
      setSuccess(shouldArchive ? 'Session archived.' : 'Session restored.');
    } catch (err) {
      setManageError('Failed to update session.');
      // eslint-disable-next-line no-console
      devError('AttendanceRegister: archive toggle failed', err);
    } finally {
      setManageActionLoading(false);
    }
  };

  const handleDeleteSession = async (session) => {
    if (!session) return;
    const confirmed = window.confirm('Delete this session? This cannot be undone.');
    if (!confirmed) return;
    const ok = await requireAdminPassword();
    if (!ok) return;

    try {
      setManageActionLoading(true);
      const attendanceQuery = query(
        collection(db, 'attendance'),
        where('sessionId', '==', session.id),
        limit(200)
      );
      const attendanceSnap = await getDocs(attendanceQuery);
      const batch = writeBatch(db);
      attendanceSnap.docs.forEach((docSnap) => batch.delete(docSnap.ref));
      await batch.commit();
      await deleteDoc(doc(db, 'sessions', session.id));
      setOverrideRefreshKey((prev) => prev + 1);
      setSuccess('Session deleted.');
    } catch (err) {
      setManageError('Failed to delete session.');
      // eslint-disable-next-line no-console
      devError('AttendanceRegister: delete failed', err);
    } finally {
      setManageActionLoading(false);
    }
  };

  const handleArchiveSession = async () => {
    if (!overrideSessionId) return;
    const target = overrideSessions.find((session) => session.id === overrideSessionId);
    if (!target) return;
    const confirmLabel = target.title || target.moduleName || 'this session';
    const confirmed = window.confirm(`Archive ${confirmLabel}? It will be removed from dropdowns.`);
    if (!confirmed) return;

    try {
      setArchivingSession(true);
      setArchiveError('');
      await updateDoc(doc(db, 'sessions', overrideSessionId), {
        archived: true,
        archivedAt: serverTimestamp(),
        archivedBy: currentUser?.uid || null
      });
      setOverrideSessionId('');
      setOverrideRefreshKey((prev) => prev + 1);
      setSuccess('Session archived.');
    } catch (err) {
      setArchiveError('Failed to archive session.');
      // eslint-disable-next-line no-console
      devError('AttendanceRegister: archive failed', err);
    } finally {
      setArchivingSession(false);
    }
  };

  useEffect(() => {
    const loadAttendanceHistory = async () => {
      try {
        if (learners.length === 0) {
          setAttendanceHistory({});
          setAttendanceDraft({});
          return;
        }

        const startDate = new Date(dateRange.start);
        const endDate = new Date(dateRange.end);
        const startTimestamp = Timestamp.fromDate(startDate);
        const endTimestamp = Timestamp.fromDate(new Date(endDate.getTime() + 24 * 60 * 60 * 1000));

        const records = [];

        if (selectedCohort) {
          const attendanceQuery = query(
            collection(db, 'attendance'),
            where('cohortId', '==', selectedCohort),
            where('date', '>=', startTimestamp),
            where('date', '<=', endTimestamp),
            orderBy('date', 'asc')
          );

          const snapshot = await getDocs(attendanceQuery);
          snapshot.docs.forEach((docSnap) => {
            records.push({
              id: docSnap.id,
              ...docSnap.data(),
              date: docSnap.data().date?.toDate?.() || new Date(docSnap.data().date || Date.now())
            });
          });
        } else {
          const chunks = chunkArray(learners.map((learner) => learner.uid), 10);
          // eslint-disable-next-line no-restricted-syntax
          for (const chunk of chunks) {
            const attendanceQuery = query(
              collection(db, 'attendance'),
              where('learnerId', 'in', chunk),
              where('date', '>=', startTimestamp),
              where('date', '<=', endTimestamp),
              orderBy('date', 'asc')
            );
            // eslint-disable-next-line no-await-in-loop
            const snapshot = await getDocs(attendanceQuery);
            snapshot.docs.forEach((docSnap) => {
              records.push({
                id: docSnap.id,
                ...docSnap.data(),
                date: docSnap.data().date?.toDate?.() || new Date(docSnap.data().date || Date.now())
              });
            });
          }
        }

        const history = {};
        records.forEach((record) => {
          const key = `${record.learnerId}_${record.date.toISOString().slice(0, 10)}`;
          history[key] = record;
        });

        setAttendanceHistory(history);

        const draftForDate = {};
        learners.forEach((learner) => {
          const key = `${learner.uid}_${selectedDate}`;
          draftForDate[learner.uid] = history[key]?.status || 'not_marked';
        });
        setAttendanceDraft(draftForDate);
      } catch (historyError) {
        setError(historyError.message || 'Failed to load attendance history.');
        // eslint-disable-next-line no-console
        devError('AttendanceRegister: failed to load history', historyError);
      }
    };

    loadAttendanceHistory();
  }, [learners, selectedCohort, dateRange, selectedDate]);

  useEffect(() => {
    setTeamsSyncUsed(false);
  }, [selectedDate, selectedCohort]);

  useEffect(() => {
    const loadSignatures = async () => {
      if (!selectedDate) {
        setAttendanceSignatures({});
        return;
      }
      try {
        setLoadingSignatures(true);
        const signaturesRef = collection(db, 'attendance_signatures');
        const snapshot = await getDocs(query(signaturesRef, where('signedDate', '==', selectedDate)));
        const signatures = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            submittedAt: data.submittedAt?.toDate ? data.submittedAt.toDate() : data.submittedAt
          };
        });
        const filtered = selectedCohort
          ? signatures.filter((signature) => signature.cohortId === selectedCohort)
          : signatures;
        const signatureMap = {};
        filtered.forEach((signature) => {
          if (signature.learnerId) {
            signatureMap[signature.learnerId] = signature;
          }
          if (signature.idNumber) {
            const normalized = String(signature.idNumber).replace(/\D/g, '');
            if (normalized) {
              signatureMap[`id_${normalized}`] = signature;
            }
          }
          if (signature.learnerEmail) {
            signatureMap[`email_${String(signature.learnerEmail).toLowerCase()}`] = signature;
          }
        });
        setAttendanceSignatures(signatureMap);
      } catch (error) {
        devError('AttendanceRegister: failed to load signatures', error);
        setAttendanceSignatures({});
      } finally {
        setLoadingSignatures(false);
      }
    };

    loadSignatures();
  }, [selectedDate, selectedCohort]);

  const dates = useMemo(
    () => getDatesInRange(dateRange.start, dateRange.end, selectedWeekdays),
    [dateRange.start, dateRange.end, selectedWeekdays]
  );

  const cohortNameById = useMemo(() => {
    const map = {};
    cohorts.forEach((cohort) => {
      map[cohort.id] = cohort.name || cohort.cohortName || cohort.id;
    });
    return map;
  }, [cohorts]);

  const groupedLearners = useMemo(() => {
    if (selectedCohort) {
      return [
        {
          cohortId: selectedCohort,
          cohortName: cohortNameById[selectedCohort] || selectedCohort,
          learners
        }
      ];
    }
    const groups = {};
    learners.forEach((learner) => {
      const cohortId = learner.cohortId || 'unassigned';
      if (!groups[cohortId]) {
        groups[cohortId] = {
          cohortId,
          cohortName: cohortNameById[cohortId] || cohortId,
          learners: []
        };
      }
      groups[cohortId].learners.push(learner);
    });
    return Object.values(groups);
  }, [learners, selectedCohort, cohortNameById]);

  const getAttendanceForDate = (learnerId, date) => {
    const key = `${learnerId}_${date}`;
    return attendanceHistory[key]?.status || 'not_marked';
  };

  const allUploadedRegisters = useMemo(() => {
    const rows = Object.values(registerUploads)
      .map((item) => ({
        ...item,
        cohortName: cohortNameById[item.cohortId] || item.cohortName || item.cohortId || 'Unassigned'
      }))
      .sort((a, b) => String(b.sessionDate || '').localeCompare(String(a.sessionDate || '')));

    if (!registerSearchTerm.trim()) return rows;
    const term = registerSearchTerm.trim().toLowerCase();
    return rows.filter((item) =>
      String(item.learnerName || '').toLowerCase().includes(term) ||
      String(item.learnerId || '').toLowerCase().includes(term) ||
      String(item.cohortName || '').toLowerCase().includes(term) ||
      String(item.sessionDate || '').toLowerCase().includes(term)
    );
  }, [registerUploads, cohortNameById, registerSearchTerm]);

  const calendarMatchedLearners = useMemo(() => {
    const term = calendarSearchTerm.trim().toLowerCase();
    if (!term) return [];
    return learners.filter((learner) => {
      const name = String(learner.displayName || '').toLowerCase();
      const email = String(learner.email || '').toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }, [learners, calendarSearchTerm]);

  const focusedCalendarLearner = useMemo(() => {
    if (!calendarMatchedLearners.length) return null;
    if (focusedCalendarLearnerId) {
      const exact = calendarMatchedLearners.find((learner) => learner.uid === focusedCalendarLearnerId);
      if (exact) return exact;
    }
    return calendarMatchedLearners[0];
  }, [calendarMatchedLearners, focusedCalendarLearnerId]);

  const focusedLearnerDateRows = useMemo(() => {
    if (!focusedCalendarLearner) return [];
    return dates
      .map((date) => {
        const status = getAttendanceForDate(focusedCalendarLearner.uid, date);
        const upload = registerUploads[`${focusedCalendarLearner.uid}_${date}`];
        return { date, status, upload };
      })
      .filter((row) => row.status !== 'not_marked' || row.upload);
  }, [focusedCalendarLearner, dates, attendanceHistory, attendanceDraft, registerUploads]);

  const missingRegisterByLearner = useMemo(() => {
    const result = {};
    learners.forEach((learner) => {
      if (!learner?.uid || !learner?.email) return;
      const missingDates = dates.filter((date) => {
        const status = getAttendanceForDate(learner.uid, date);
        const hasUpload = Boolean(registerUploads[`${learner.uid}_${date}`]);
        return status === 'present' && !hasUpload;
      });
      if (missingDates.length > 0) {
        result[learner.uid] = {
          learner,
          missingDates
        };
      }
    });
    return result;
  }, [learners, dates, registerUploads, attendanceHistory, attendanceDraft]);

  const missingRegisterReminderCount = useMemo(
    () => Object.values(missingRegisterByLearner).reduce((sum, item) => sum + item.missingDates.length, 0),
    [missingRegisterByLearner]
  );

  const downloadCsv = (rows, filename) => {
    const content = rows
      .map((row) =>
        row
          .map((cell) => {
            const value = cell == null ? '' : String(cell);
            const escaped = value.replace(/"/g, '""');
            return `"${escaped}"`;
          })
          .join(',')
      )
      .join('\n');
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExportRegister = () => {
    if (learners.length === 0) return;
    setExporting(true);
    try {
      if (viewMode === 'calendar') {
        const header = ['Learner', 'Email', ...dates];
        const rows = learners.map((learner) => {
          const row = [learner.displayName || learner.uid, learner.email || ''];
          dates.forEach((date) => {
            const record = attendanceHistory[`${learner.uid}_${date}`];
            const status = record?.status || 'not_marked';
            const uploadKey = `${learner.uid}_${date}`;
            const registerUploaded = registerUploads[uploadKey] ? 'register_uploaded' : 'no_register';
            row.push(`${status} | ${registerUploaded}`);
          });
          return row;
        });
        downloadCsv(
          [header, ...rows],
          `attendance_register_${selectedCohort || 'all'}_${dateRange.start}_${dateRange.end}.csv`
        );
      } else {
        const header = ['Learner', 'Email', 'Status', 'Source', 'Marked At', 'Signed', 'Register Uploaded'];
        const rows = learners.map((learner) => {
          const record = attendanceHistory[`${learner.uid}_${selectedDate}`];
          const status = record?.status || attendanceDraft[learner.uid] || 'not_marked';
          const source = record?.source || (status !== 'not_marked' ? 'draft' : '');
          const markedAt = record?.markedAt ? new Date(record.markedAt).toLocaleString() : '';
          const signed = getSignatureForLearner(learner, selectedDate) ? 'yes' : 'no';
          const registerUploaded = registerUploads[`${learner.uid}_${selectedDate}`] ? 'yes' : 'no';
          return [learner.displayName || learner.uid, learner.email || '', status, source, markedAt, signed, registerUploaded];
        });
        downloadCsv(
          [header, ...rows],
          `attendance_register_${selectedCohort || 'all'}_${selectedDate}.csv`
        );
      }
    } finally {
      setExporting(false);
    }
  };

  const handleStatusChange = (learnerId, status) => {
    setAttendanceDraft((prev) => ({
      ...prev,
      [learnerId]: status
    }));
  };

  const handleRegisterUpload = async (learner, file, sessionDateOverride = null) => {
    const sessionDate = sessionDateOverride || selectedDate;
    if (!learner || !file || !sessionDate) return;
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    if (!allowedRegisterExtensions.includes(extension)) {
      setError('Only PDF, DOC, or DOCX files are allowed for register uploads.');
      return;
    }
    if (file.size > maxRegisterBytes) {
      setError('Register upload exceeds 10MB limit.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const existingUploadKey = `${learner.uid}_${sessionDate}`;
      const existingUpload = registerUploads[existingUploadKey];

      // Replace flow: remove old file from storage when uploading a corrected register.
      if (existingUpload?.filePath) {
        try {
          await deleteObject(ref(storage, existingUpload.filePath));
        } catch (cleanupError) {
          devLog('AttendanceRegister: old register cleanup skipped', cleanupError);
        }
      }

      const filePath = `attendanceRegisters/${learner.uid}/${sessionDate}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, filePath);
      await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(storageRef);

      const registerDoc = {
        learnerId: learner.uid,
        learnerName: learner.displayName || learner.email || learner.uid,
        cohortId: selectedCohort || learner.cohortId || null,
        programId: selectedProgram || learner.programId || null,
        sessionDate,
        fileName: file.name,
        fileUrl,
        filePath,
        uploadedBy: auth.currentUser?.uid || null,
        uploadedByName: userProfile?.displayName || auth.currentUser?.email || 'Staff',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(
        doc(db, 'attendance_register_uploads', `${learner.uid}_${sessionDate}`),
        registerDoc,
        { merge: true }
      );

      // Cleanup legacy duplicates for same learner/date so old records don't re-appear.
      const canonicalDocId = `${learner.uid}_${sessionDate}`;
      const duplicatesSnapshot = await getDocs(
        query(collection(db, 'attendance_register_uploads'), where('learnerId', '==', learner.uid))
      );
      for (const docSnap of duplicatesSnapshot.docs) {
        if (docSnap.id === canonicalDocId) continue;
        const data = docSnap.data();
        const normalizedDate = normalizeSessionDateValue(data?.sessionDate);
        if (normalizedDate !== sessionDate) continue;
        if (data?.filePath) {
          try {
            await deleteObject(ref(storage, data.filePath));
          } catch (cleanupError) {
            devLog('AttendanceRegister: duplicate storage cleanup skipped', cleanupError);
          }
        }
        await deleteDoc(docSnap.ref);
      }

      setRegisterUploads((prev) => ({
        ...prev,
        [`${learner.uid}_${sessionDate}`]: {
          ...registerDoc,
          id: canonicalDocId
        }
      }));
      setSuccess('Register uploaded.');
    } catch (uploadError) {
      devError('AttendanceRegister: failed to upload register', uploadError);
      setError('Failed to upload register. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveRegisterUpload = async (learner, sessionDateOverride = null) => {
    const sessionDate = sessionDateOverride || selectedDate;
    if (!learner || !sessionDate) return;
    const uploadKey = `${learner.uid}_${sessionDate}`;
    const existingUpload = registerUploads[uploadKey];
    if (!existingUpload) return;

    const confirmed = window.confirm(
      `Remove register for ${learner.displayName || learner.uid} on ${sessionDate}?`
    );
    if (!confirmed) return;

    try {
      setSaving(true);
      setError('');
      // Delete all matching records for this learner/date (handles legacy duplicate docs).
      const duplicateSnapshot = await getDocs(
        query(collection(db, 'attendance_register_uploads'), where('learnerId', '==', learner.uid))
      );
      const targets = duplicateSnapshot.docs.filter((docSnap) => {
        const data = docSnap.data();
        const normalizedDate = normalizeSessionDateValue(data?.sessionDate);
        return normalizedDate === sessionDate;
      });

      // Ensure deterministic ID record is also targeted even if learnerId missing in legacy data.
      const canonicalRef = doc(db, 'attendance_register_uploads', `${learner.uid}_${sessionDate}`);
      const hasCanonical = targets.some((docSnap) => docSnap.id === canonicalRef.id);
      if (!hasCanonical) {
        try {
          const canonicalSnap = await getDocs(
            query(collection(db, 'attendance_register_uploads'), where('sessionDate', '==', sessionDate))
          );
          canonicalSnap.docs.forEach((docSnap) => {
            if (docSnap.id === canonicalRef.id) {
              targets.push(docSnap);
            }
          });
        } catch (canonicalLookupError) {
          devLog('AttendanceRegister: canonical lookup skipped', canonicalLookupError);
        }
      }

      for (const targetDoc of targets) {
        const data = targetDoc.data();
        if (data?.filePath) {
          try {
            await deleteObject(ref(storage, data.filePath));
          } catch (storageError) {
            devLog('AttendanceRegister: failed to remove storage file', storageError);
          }
        }
        await deleteDoc(targetDoc.ref);
      }

      // Final fallback delete for deterministic doc id.
      try {
        await deleteDoc(canonicalRef);
      } catch (deleteError) {
        // ignore not-found-like conditions
      }

      setRegisterUploads((prev) => {
        const next = { ...prev };
        delete next[uploadKey];
        return next;
      });
      setSuccess(`Register removed${targets.length > 1 ? ` (${targets.length} duplicate records cleaned)` : ''}.`);
    } catch (removeError) {
      devError('AttendanceRegister: failed to remove register', removeError);
      setError('Failed to remove register.');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkRegisterUpload = async () => {
    if (!bulkRegisterDate || !bulkRegisterFile) {
      setError('Please select a date and file for bulk upload.');
      return;
    }

    const dateLearners = learners.filter(
      (learner) => getAttendanceForDate(learner.uid, bulkRegisterDate) === 'present'
    );

    if (dateLearners.length === 0) {
      setError('No learners marked present for the selected date.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      for (const learner of dateLearners) {
        await handleRegisterUpload(learner, bulkRegisterFile, bulkRegisterDate);
      }
      setSuccess(`Register uploaded for ${dateLearners.length} learners.`);
      setBulkRegisterFile(null);
    } catch (bulkError) {
      devError('Bulk register upload failed:', bulkError);
      setError('Bulk upload failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkDownloadRegisters = async () => {
    const entries = Object.values(registerUploads);
    if (!entries.length) {
      setError('No uploaded registers found for the current view.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const learnerMap = new Map(
        learners.map((learner) => [learner.uid, learner.displayName || learner.email || learner.uid])
      );

      let downloaded = 0;
      for (const item of entries) {
        if (!item?.fileUrl || !item?.learnerId) continue;
        const learnerName = sanitizeFilePart(learnerMap.get(item.learnerId) || item.learnerName || item.learnerId);
        const sessionDate = sanitizeFilePart(item.sessionDate || 'unknown_date');
        const originalName = sanitizeFilePart(item.fileName || 'register');
        const downloadName = `${learnerName}/${sessionDate}__${originalName}`;

        try {
          const response = await fetch(item.fileUrl);
          if (!response.ok) continue;
          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = downloadName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(objectUrl);
          downloaded += 1;
          await new Promise((resolve) => setTimeout(resolve, UI_TIMEOUTS.SIMULATED_DELAY_MS));
        } catch (downloadError) {
          devLog('AttendanceRegister: bulk download skipped item', downloadError);
        }
      }
      setSuccess(`Downloaded ${downloaded} register file(s).`);
    } catch (bulkDownloadError) {
      devError('AttendanceRegister: bulk register download failed', bulkDownloadError);
      setError('Failed to bulk download registers.');
    } finally {
      setSaving(false);
    }
  };

  const handleClearMyUploadedRegisters = async () => {
    const uploaderUid = auth.currentUser?.uid || currentUser?.uid;
    if (!uploaderUid) {
      setError('Unable to identify current user for register cleanup.');
      return;
    }

    try {
      setClearingMyRegisters(true);
      setError('');
      setSuccess('');

      const snapshot = await getDocs(
        query(collection(db, 'attendance_register_uploads'), where('uploadedBy', '==', uploaderUid))
      );
      if (snapshot.empty) {
        setError('No register uploads found for your account.');
        return;
      }

      const confirmed = window.confirm(
        `Remove all register uploads you added across all cohorts (${snapshot.size}) from the system and storage?`
      );
      if (!confirmed) return;

      let deletedCount = 0;
      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        if (data?.filePath) {
          try {
            await deleteObject(ref(storage, data.filePath));
          } catch (storageError) {
            devLog('AttendanceRegister: failed to remove register storage file', storageError);
          }
        }
        await deleteDoc(docSnap.ref);
        deletedCount += 1;
      }

      setRegisterUploads((prev) => {
        const next = {};
        Object.entries(prev).forEach(([key, value]) => {
          if (value?.uploadedBy !== uploaderUid) {
            next[key] = value;
          }
        });
        return next;
      });

      setSuccess(`Removed ${deletedCount} register upload(s) you added.`);
    } catch (cleanupError) {
      devError('AttendanceRegister: failed to clear uploader registers', cleanupError);
      setError('Failed to clear your uploaded registers.');
    } finally {
      setClearingMyRegisters(false);
    }
  };

  const getSignatureForLearner = (learner) => {
    if (!learner) return null;
    if (attendanceSignatures[learner.uid]) return attendanceSignatures[learner.uid];
    const idNumber = learner.idNumber ? String(learner.idNumber).replace(/\D/g, '') : '';
    if (idNumber && attendanceSignatures[`id_${idNumber}`]) {
      return attendanceSignatures[`id_${idNumber}`];
    }
    if (learner.email) {
      const emailKey = `email_${String(learner.email).toLowerCase()}`;
      if (attendanceSignatures[emailKey]) {
        return attendanceSignatures[emailKey];
      }
    }
    return null;
  };

  const normalizeMatchValue = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');

  const resolveLearnerFromTeamsRecord = (record) => {
    if (!record) return null;

    const byUid = learners.find((learner) => learner.uid === record.learnerId);
    if (byUid) return byUid;

    const recordEmail = normalizeMatchValue(record.email);
    if (recordEmail) {
      const byEmail = learners.find(
        (learner) => normalizeMatchValue(learner.email) === recordEmail
      );
      if (byEmail) return byEmail;
    }

    const recordName = normalizeMatchValue(record.learnerName);
    if (recordName) {
      const byName = learners.find((learner) => {
        const displayName = normalizeMatchValue(learner.displayName);
        const fullName = normalizeMatchValue(
          `${learner.firstName || ''} ${learner.lastName || learner.surname || ''}`
        );
        return recordName === displayName || recordName === fullName;
      });
      if (byName) return byName;
    }

    return null;
  };

  const handleSave = async () => {
    if (!allowSessionCreateAndMark) {
      setError('You do not have permission to create sessions or mark attendance. Use "Override Attendance" on an existing session.');
      return;
    }
    const cohortProgramId = selectedCohort
      ? cohorts.find((cohort) => cohort.id === selectedCohort)?.programId
      : null;
    const resolvedProgramId = selectedProgram || cohortProgramId;
    if (!resolvedProgramId || !selectedDate) {
      setError('Please select both a program (or cohort) and a session date.');
      return;
    }

    if (learners.length === 0) {
      setError('No learners available for attendance marking.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const programInfo = programs.find((program) => program.id === resolvedProgramId);
      const sessionDate = new Date(selectedDate);

      const sessionResult = await attendanceService.createSession({
        cohortId: selectedCohort || null,
        moduleId: null,
        moduleName: null,
        programId: resolvedProgramId,
        programName: programInfo?.name || programInfo?.title || resolvedProgramId,
        scheduledDate: sessionDate,
        facilitatorId: auth.currentUser?.uid || null,
        createdBy: auth.currentUser?.uid || 'system',
        title: `${programInfo?.name || programInfo?.title || 'Session'} - ${selectedDate}`,
        status: 'completed',
        requiresAttendanceConfirmation: requestLearnerConfirmation,
        confirmationRequested: requestLearnerConfirmation
      });

      if (!sessionResult.success) {
        throw new Error(sessionResult.error || 'Failed to create attendance session.');
      }

      if (requestLearnerConfirmation) {
        const sessionLabel = `${programInfo?.name || programInfo?.title || 'Session'} (${selectedDate})`;
        learners.forEach((learner) => {
          if (isActiveLearner(learner)) {
            NotificationService.notifyAttendanceConfirmation(learner, sessionLabel);
          }
        });
        setSuccess('Session created. Learners have been asked to confirm attendance.');
        return;
      }

      const attendancePayload = learners.map((learner) => ({
        learnerId: learner.uid,
        status: attendanceDraft[learner.uid] || 'not_marked',
        cohortId: selectedCohort || learner.cohortId || null,
        moduleId: null,
        programId: resolvedProgramId,
        date: sessionDate,
        source: teamsSyncUsed ? 'teams-manual' : 'manual'
      }));

      const markResult = await attendanceService.markAttendance(
        sessionResult.sessionId,
        attendancePayload,
        auth.currentUser?.uid || 'system'
      );

      if (!markResult.success) {
        throw new Error(markResult.error || 'Failed to record attendance.');
      }

      const updatedHistory = { ...attendanceHistory };
      attendancePayload.forEach((record) => {
        const key = `${record.learnerId}_${selectedDate}`;
        updatedHistory[key] = {
          ...record,
          markedAt: new Date(),
          markedBy: auth.currentUser?.uid || 'system'
        };
      });
      setAttendanceHistory(updatedHistory);

      NotificationService.notifyAttendanceMarked(
        `${programInfo?.name || programInfo?.title || 'Session'} (${selectedDate})`,
        attendancePayload.length
      );

      const sessionLabel = `${programInfo?.name || programInfo?.title || 'Session'} (${selectedDate})`;
      const presentLearners = attendancePayload
        .filter((record) => record.status === 'present')
        .map((record) => learners.find((item) => item.uid === record.learnerId))
        .filter((learner) => learner && isActiveLearner(learner));
      const activeLearnerCount = learners.filter((learner) => isActiveLearner(learner)).length;
      setLastMarkedSession({
        id: sessionResult.sessionId,
        label: sessionLabel,
        sessionDate: selectedDate
      });
      setLastMarkedPresentLearners(presentLearners);
      attendancePayload.forEach((record) => {
        const learner = learners.find((item) => item.uid === record.learnerId);
        if (learner && isActiveLearner(learner)) {
          NotificationService.notifyAttendanceConfirmation(learner, sessionLabel);
        }
      });

      attendancePayload
        .filter((record) => record.status === 'absent')
        .forEach((record) => {
          const learner = learners.find((item) => item.uid === record.learnerId);
          if (learner) {
          NotificationService.notifyLowAttendance(learner, 0);
        }
      });

      setSuccess(`Attendance recorded successfully. Present: ${presentLearners.length}/${activeLearnerCount}.`);
    } catch (saveError) {
      setError(saveError.message || 'Failed to save attendance.');
      // eslint-disable-next-line no-console
      devError('AttendanceRegister: failed to save', saveError);
    } finally {
      setSaving(false);
    }
  };

  const getCurrentPresentLearners = () => (
    learners
      .filter((learner) => attendanceDraft[learner.uid] === 'present')
      .filter((learner) => learner && isActiveLearner(learner))
  );

  const handleSendRegisterLink = async () => {
    const presentLearners = getCurrentPresentLearners();
    if (!lastMarkedSession || presentLearners.length === 0) {
      setError('No present learners found to notify.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const jotformLink = process.env.REACT_APP_GOOGLE_FORMS_ATTENDANCE_URL || '';
      const sessionDateLabel = lastMarkedSession?.sessionDate || selectedDate || 'the session date provided by your facilitator';
      await Promise.all(
        presentLearners.map((learner) =>
          NotificationService.notifyAttendanceRegisterSign(
            learner,
            lastMarkedSession.label,
            lastMarkedSession.id,
            sessionDateLabel
          )
        )
      );

      const recipientEmails = presentLearners
        .map((learner) => learner.email)
        .filter(Boolean);
      if (recipientEmails.length > 0) {
        const sendCustomEmail = httpsCallable(functions, 'sendCustomEmail');
        const subject = `Attendance Register: ${lastMarkedSession.label}`;
        const htmlContent = `
          <p>Hello,</p>
          <p>Please sign the attendance register for <strong>${lastMarkedSession.label}</strong>.</p>
          <p><strong>Important:</strong> When completing the form, please enter this date for the session: <strong>${sessionDateLabel}</strong>.</p>
          <p><a href="${jotformLink}" target="_blank" rel="noopener noreferrer">Click here to sign the register</a></p>
          <p>If the link does not open, copy and paste this into your browser:</p>
          <p>${jotformLink}</p>
        `;
        await sendCustomEmail({
          subject,
          htmlContent,
          textContent: `Please sign the attendance register for ${lastMarkedSession.label}. Use this date on the form: ${sessionDateLabel}. ${jotformLink}`,
          recipients: recipientEmails
        });
      }
      setSuccess(`Reminder emails sent successfully. Attendance register link sent to ${presentLearners.length} learner(s) for ${lastMarkedSession.label}.`);
    } catch (notifyError) {
      setError(notifyError?.message || 'Failed to send attendance register links.');
    } finally {
      setSaving(false);
    }
  };

  const handleSendMissingRegisterReminders = async () => {
    const entries = Object.values(missingRegisterByLearner);
    if (entries.length === 0) {
      setError('No learners with missing register uploads for the selected range.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const jotformLink = process.env.REACT_APP_GOOGLE_FORMS_ATTENDANCE_URL || '';
      const sendCustomEmail = httpsCallable(functions, 'sendCustomEmail');

      await Promise.all(
        entries.map(async ({ learner, missingDates }) => {
          const datesList = missingDates.map((d) => `<li>${d}</li>`).join('');
          const subject = 'Reminder: Attendance register still outstanding';
          const htmlContent = `
            <p>Hello ${learner.displayName || ''},</p>
            <p>Please complete and sign your attendance register for the following date(s):</p>
            <ul>${datesList}</ul>
            <p>Use this link to sign the register:</p>
            <p><a href="${jotformLink}" target="_blank" rel="noopener noreferrer">${jotformLink}</a></p>
            <p>Important: Submit one form per date listed above and use the exact session date each time.</p>
          `;
          const textContent = `Please sign your attendance register for these dates: ${missingDates.join(', ')}. Link: ${jotformLink}`;

          await sendCustomEmail({
            subject,
            htmlContent,
            textContent,
            recipients: [learner.email]
          });

          const firstDate = missingDates[0];
          await NotificationService.notifyAttendanceRegisterSign(
            learner,
            'attendance reminder',
            `missing-register-${learner.uid}`,
            firstDate
          );
        })
      );

      const totalDates = entries.reduce((sum, item) => sum + item.missingDates.length, 0);
      setSuccess(`Reminder emails sent successfully. Delivery summary: ${entries.length} learner(s), ${totalDates} outstanding register date(s).`);
    } catch (notifyError) {
      devError('AttendanceRegister: failed to send missing register reminders', notifyError);
      setError(notifyError?.message || 'Failed to send missing register reminders.');
    } finally {
      setSaving(false);
    }
  };

  const handleSendSingleMissingRegisterReminder = async (learnerId) => {
    const entry = missingRegisterByLearner[learnerId];
    if (!entry) {
      setError('No missing register reminder is available for that learner.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const jotformLink = process.env.REACT_APP_GOOGLE_FORMS_ATTENDANCE_URL || '';
      const sendCustomEmail = httpsCallable(functions, 'sendCustomEmail');
      const { learner, missingDates } = entry;
      const datesList = missingDates.map((d) => `<li>${d}</li>`).join('');

      await sendCustomEmail({
        subject: 'Reminder: Attendance register still outstanding',
        htmlContent: `
          <p>Hello ${learner.displayName || ''},</p>
          <p>Please complete and sign your attendance register for the following date(s):</p>
          <ul>${datesList}</ul>
          <p>Use this link to sign the register:</p>
          <p><a href="${jotformLink}" target="_blank" rel="noopener noreferrer">${jotformLink}</a></p>
          <p>Important: Submit one form per date listed above and use the exact session date each time.</p>
        `,
        textContent: `Please sign your attendance register for these dates: ${missingDates.join(', ')}. Link: ${jotformLink}`,
        recipients: [learner.email]
      });

      await NotificationService.notifyAttendanceRegisterSign(
        learner,
        'attendance reminder',
        `missing-register-${learner.uid}`,
        missingDates[0]
      );

      setSuccess(`Reminder email sent successfully to ${learner.displayName || learner.email} for ${missingDates.length} outstanding date(s).`);
    } catch (notifyError) {
      devError('AttendanceRegister: failed to send single missing register reminder', notifyError);
      setError(notifyError?.message || 'Failed to send learner reminder.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="shadow-sm border-0">
        <Card.Body className="text-center text-muted py-5">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading attendance register...
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm border-0">
      <Card.Header className="bg-white d-flex flex-wrap justify-content-between align-items-center">
        <div>
          <h5 className="mb-0">Attendance Register</h5>
          <small className="text-muted">
            Switch between calendar overview and single-session capture.
          </small>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <ButtonGroup>
            <Button
              variant={viewMode === 'calendar' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setViewMode('calendar')}
            >
              Calendar View
            </Button>
            {!externalFacilitatorLimited && (
              <Button
                variant={viewMode === 'single' ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => setViewMode('single')}
              >
                Single Session
              </Button>
            )}
          </ButtonGroup>
          <Button
            variant="outline-success"
            size="sm"
            onClick={handleExportRegister}
            disabled={exporting || learners.length === 0}
          >
            {exporting ? 'Exporting...' : 'Download Register'}
          </Button>
          {!externalFacilitatorLimited && (
            <Button variant="outline-secondary" size="sm" onClick={() => setShowTeamsSync(true)}>
              Sync from Teams
            </Button>
          )}
        </div>
      </Card.Header>
      <Card.Body>
        {error && (
          <Alert variant="danger" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" onClose={() => setSuccess('')} dismissible>
            {success}
          </Alert>
        )}

	            <Row className="mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Program</Form.Label>
              <Form.Select
                value={selectedProgram}
                onChange={(e) => {
                  setSelectedProgram(e.target.value);
                  setSelectedCohort('');
                }}
                onWheel={(e) => e.currentTarget.blur()}
              >
                <option value="">Select Program</option>
                {programs.map((program) => (
                  <option key={program.id} value={program.id}>
                    {program.name || program.title || program.id}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Cohort</Form.Label>
            <Form.Select
              value={selectedCohort}
              onChange={(e) => setSelectedCohort(e.target.value)}
              onWheel={(e) => e.currentTarget.blur()}
            >
              <option value="">Select cohort</option>
              {cohorts
                .filter((cohort) => !selectedProgram || cohort.programId === selectedProgram)
                .map((cohort) => (
                <option key={cohort.id} value={cohort.id}>
                  {cohort.name || cohort.cohortName || cohort.id}
                </option>
              ))}
            </Form.Select>
            </Form.Group>
          </Col>
          {viewMode === 'single' ? (
            <Col md={4}>
              <Form.Group>
                <Form.Label>Session Date</Form.Label>
                <Form.Control
                  type="date"
                  value={selectedDate}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    setSelectedDate(newDate);
                    const draftForDate = {};
                    learners.forEach((learner) => {
                      const key = `${learner.uid}_${newDate}`;
                      draftForDate[learner.uid] = attendanceHistory[key]?.status || 'not_marked';
                    });
                    setAttendanceDraft(draftForDate);
                  }}
                  onWheel={(e) => e.currentTarget.blur()}
                />
              </Form.Group>
            </Col>
          ) : (
            <>
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))}
                    onWheel={(e) => e.currentTarget.blur()}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))}
                    onWheel={(e) => e.currentTarget.blur()}
                  />
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group>
                  <Form.Label>Show Days</Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    {[
                      { label: 'Sun', value: 0 },
                      { label: 'Mon', value: 1 },
                      { label: 'Tue', value: 2 },
                      { label: 'Wed', value: 3 },
                      { label: 'Thu', value: 4 },
                      { label: 'Fri', value: 5 },
                      { label: 'Sat', value: 6 }
                    ].map((day) => (
                      <Form.Check
                        key={day.value}
                        type="checkbox"
                        id={`attendance-day-${day.value}`}
                        label={day.label}
                        checked={selectedWeekdays.has(day.value)}
                        onChange={() => {
                          setSelectedWeekdays((prev) => {
                            const next = new Set(prev);
                            if (next.has(day.value)) {
                              next.delete(day.value);
                            } else {
                              next.add(day.value);
                            }
                            return next;
                          });
                        }}
                      />
                    ))}
                  </div>
                </Form.Group>
              </Col>
            </>
          )}
        </Row>
        {viewMode === 'single' && allowSessionCreateAndMark && (
          <Row className="mb-3">
            <Col md={12}>
              <Form.Check
                type="checkbox"
                id="attendance-confirmation-request"
                label="Require learner confirmation (create session and notify learners)"
                checked={requestLearnerConfirmation}
                onChange={(e) => setRequestLearnerConfirmation(e.target.checked)}
              />
            </Col>
          </Row>
        )}

        <Card className="border-0 bg-light mb-3">
          <Card.Body>
            <Row className="align-items-end">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>All Uploaded Registers (System)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Search by learner, cohort or date"
                    value={registerSearchTerm}
                    onChange={(e) => setRegisterSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                      }
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="text-end">
                <div className="d-flex justify-content-end align-items-center gap-2">
                  <small className="text-muted">
                    {loadingRegisterUploads ? 'Loading registers...' : `${allUploadedRegisters.length} register file(s) visible`}
                  </small>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={handleClearMyUploadedRegisters}
                    disabled={saving || clearingMyRegisters}
                  >
                    {clearingMyRegisters ? 'Clearing...' : 'Clear My Uploaded Registers (All Cohorts)'}
                  </Button>
                </div>
              </Col>
            </Row>
            <div className="mt-3" style={{ maxHeight: '320px', overflowY: 'auto' }}>
              <Table striped bordered hover responsive size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Learner</th>
                    <th>Cohort</th>
                    <th>Uploaded By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allUploadedRegisters.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center text-muted">
                        No uploaded registers found.
                      </td>
                    </tr>
                  ) : (
                    allUploadedRegisters.map((item) => (
                      <tr key={`${item.learnerId}_${item.sessionDate}_${item.id || item.filePath || ''}`}>
                        <td>{item.sessionDate || '-'}</td>
                        <td>{item.learnerName || item.learnerId || '-'}</td>
                        <td>{item.cohortName}</td>
                        <td>{item.uploadedByName || '-'}</td>
                        <td>
                          <div className="d-flex gap-2">
                            {item.fileUrl ? (
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => window.open(item.fileUrl, '_blank')}
                              >
                                View
                              </Button>
                            ) : null}
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleRemoveRegisterUpload(
                                { uid: item.learnerId, displayName: item.learnerName || item.learnerId },
                                item.sessionDate
                              )}
                              disabled={saving || !item.learnerId || !item.sessionDate}
                            >
                              Remove
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>

        {viewMode === 'calendar' ? (
          <>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Search Learner</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Search by name or email"
                    value={calendarSearchTerm}
                    onChange={(e) => setCalendarSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                      }
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Bulk Register Upload (Present Only)</Form.Label>
                  <Row className="g-2">
                    <Col md={4}>
                      <Form.Control
                        type="date"
                        value={bulkRegisterDate}
                        onChange={(e) => setBulkRegisterDate(e.target.value)}
                      />
                    </Col>
                    <Col md={5}>
                      <Form.Control
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) => setBulkRegisterFile(e.target.files?.[0] || null)}
                      />
                    </Col>
                    <Col md={3}>
                      <Button
                        variant="outline-primary"
                        onClick={handleBulkRegisterUpload}
                        disabled={saving || !bulkRegisterDate || !bulkRegisterFile}
                      >
                        Upload
                      </Button>
                    </Col>
                    <Col md={3}>
                      <Button
                        variant="outline-secondary"
                        onClick={handleBulkDownloadRegisters}
                        disabled={saving || Object.keys(registerUploads).length === 0}
                      >
                        Bulk Download Registers
                      </Button>
                    </Col>
                    <Col md={4}>
                      <Button
                        variant="outline-warning"
                        onClick={handleSendMissingRegisterReminders}
                        disabled={saving || missingRegisterReminderCount === 0}
                      >
                        Send Missing Register Reminders ({missingRegisterReminderCount})
                      </Button>
                    </Col>
                  </Row>
                </Form.Group>
              </Col>
	            </Row>
            {calendarSearchTerm.trim() && (
              <Card className="mb-3 border-primary-subtle">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>Learner Quick Register View</strong>
                    <div className="small text-muted">
                      Search one learner and manage all marked dates in one place
                    </div>
                  </div>
                  <Badge bg="info">{calendarMatchedLearners.length} match(es)</Badge>
                </Card.Header>
                <Card.Body>
                  {calendarMatchedLearners.length === 0 ? (
                    <Alert variant="warning" className="mb-0">
                      No learner matches the current search.
                    </Alert>
                  ) : (
                    <>
                      {calendarMatchedLearners.length > 1 && (
                        <Row className="mb-3">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Select Learner</Form.Label>
                              <Form.Select
                                value={focusedCalendarLearner?.uid || ''}
                                onChange={(e) => setFocusedCalendarLearnerId(e.target.value)}
                              >
                                {calendarMatchedLearners.map((learner) => (
                                  <option key={learner.uid} value={learner.uid}>
                                    {learner.displayName || learner.uid} ({learner.email || 'No email'})
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>
                      )}

                      {focusedCalendarLearner && (
                        <>
                          <div className="mb-2">
                            <strong>{focusedCalendarLearner.displayName || focusedCalendarLearner.uid}</strong>
                            <div className="small text-muted">{focusedCalendarLearner.email || 'No email'}</div>
                          </div>
                          <Table striped bordered hover responsive size="sm" className="mb-0">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Status</th>
                                <th>Register</th>
                                <th>Attach / Replace</th>
                                <th>Reminder</th>
                              </tr>
                            </thead>
                            <tbody>
                              {focusedLearnerDateRows.length === 0 ? (
                                <tr>
                                  <td colSpan={5} className="text-center text-muted">
                                    No marked dates for the selected range.
                                  </td>
                                </tr>
                              ) : (
                                focusedLearnerDateRows.map((row) => (
                                  <tr key={`${focusedCalendarLearner.uid}_${row.date}`}>
                                    <td>{row.date}</td>
                                    <td>
                                      <Badge bg={getAttendanceStatusMeta(row.status).badge}>
                                        {getAttendanceStatusMeta(row.status).label}
                                      </Badge>
                                    </td>
                                    <td>
                                      <div className="d-flex gap-2 flex-wrap">
                                        {row.upload?.fileUrl ? (
                                          <>
                                            <Button
                                              size="sm"
                                              variant="outline-secondary"
                                              onClick={() => window.open(row.upload.fileUrl, '_blank')}
                                            >
                                              View
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="outline-danger"
                                              onClick={() => handleRemoveRegisterUpload(focusedCalendarLearner, row.date)}
                                              disabled={saving}
                                            >
                                              Remove
                                            </Button>
                                          </>
                                        ) : (
                                          <small className="text-muted">
                                            {loadingRegisterUploads ? 'Loading...' : 'No file'}
                                          </small>
                                        )}
                                      </div>
                                    </td>
                                    <td>
                                      <Form.Control
                                        type="file"
                                        size="sm"
                                        accept=".pdf,.doc,.docx"
                                        onChange={(e) => handleRegisterUpload(focusedCalendarLearner, e.target.files?.[0], row.date)}
                                        disabled={saving}
                                      />
                                    </td>
                                    <td>
                                      {row.status === 'present' && !row.upload ? (
                                        <Button
                                          size="sm"
                                          variant="outline-warning"
                                          onClick={() => handleSendSingleMissingRegisterReminder(focusedCalendarLearner.uid)}
                                          disabled={saving}
                                        >
                                          Send
                                        </Button>
                                      ) : (
                                        <small className="text-muted">-</small>
                                      )}
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </Table>
                        </>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
            )}
            {(calendarSearchTerm.trim() && focusedCalendarLearner
              ? [{
                  cohortId: focusedCalendarLearner.cohortId || 'search_result',
                  cohortName: 'Search Result',
                  learners: [focusedCalendarLearner]
                }]
              : groupedLearners
            ).map((group) => {
              const filteredLearners = group.learners;
              if (filteredLearners.length === 0) return null;
              return (
              <div key={group.cohortId} className="mb-4">
                <h6 className="mb-2">
                  {group.cohortName}
                  <Badge bg="light" text="dark" className="ms-2">
                    {filteredLearners.length}
                  </Badge>
                </h6>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Learner</th>
                      {dates.map((date) => (
                        <th key={date}>{date}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLearners.map((learner) => (
                      <tr key={learner.uid}>
                        <td>
                          <div>{learner.displayName || learner.uid}</div>
                          <small className="text-muted">{learner.email}</small>
                        </td>
                        {dates.map((date) => {
                          const status = getAttendanceForDate(learner.uid, date);
                          const uploadKey = `${learner.uid}_${date}`;
                          const upload = registerUploads[uploadKey];
                          return (
                            <td key={`${learner.uid}_${date}`}>
                              <Badge bg={getAttendanceStatusMeta(status).badge}>
                                {getAttendanceStatusMeta(status).label}
                              </Badge>
                              <div className="mt-1">
                                {upload?.fileUrl ? (
                                  <div className="d-flex flex-column gap-1">
                                    <Button
                                      size="sm"
                                      variant="outline-secondary"
                                      onClick={() => window.open(upload.fileUrl, '_blank')}
                                    >
                                      Register ✔
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      onClick={() => handleRemoveRegisterUpload(learner, date)}
                                      disabled={saving}
                                    >
                                      Remove
                                    </Button>
                                    <Form.Control
                                      type="file"
                                      size="sm"
                                      accept=".pdf,.doc,.docx"
                                      onChange={(e) => handleRegisterUpload(learner, e.target.files?.[0], date)}
                                      disabled={saving}
                                    />
                                  </div>
                                ) : (
                                  <Form.Control
                                    type="file"
                                    size="sm"
                                    accept=".pdf,.doc,.docx"
                                    onChange={(e) => handleRegisterUpload(learner, e.target.files?.[0], date)}
                                    disabled={saving}
                                  />
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              );
            })}

            {allowSessionCreateAndMark && (
              <Card className="border-0 bg-light mt-3">
                <Card.Body>
                  <Row className="align-items-end">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Override Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={selectedDate}
                          onChange={(e) => {
                            const newDate = e.target.value;
                            setSelectedDate(newDate);
                            const draftForDate = {};
                            learners.forEach((learner) => {
                              const key = `${learner.uid}_${newDate}`;
                              draftForDate[learner.uid] = attendanceHistory[key]?.status || 'not_marked';
                            });
                            setAttendanceDraft(draftForDate);
                          }}
                          onWheel={(e) => e.currentTarget.blur()}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={8} className="text-end">
                      <Button
                        variant="primary"
                        onClick={handleSave}
                        disabled={saving || (!selectedProgram && !selectedCohort) || learners.length === 0}
                      >
                        {saving ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Saving...
                          </>
                        ) : (
                          'Save Overrides for Date'
                        )}
                      </Button>
                    </Col>
                  </Row>
                  <Table striped bordered hover responsive className="mt-3">
                  <thead>
                    <tr>
                      <th>Learner</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Signed</th>
                      <th>Signed At</th>
                      <th>Register</th>
                      <th>Upload Register</th>
                    </tr>
                  </thead>
                  <tbody>
                    {learners.map((learner) => (
                      <tr key={learner.uid}>
                        <td>{learner.displayName || learner.uid}</td>
                        <td>{learner.email}</td>
                        <td>
                          <ButtonGroup>
                            <Button
                              variant={attendanceDraft[learner.uid] === 'present' ? 'success' : 'outline-success'}
                              size="sm"
                              onClick={() => handleStatusChange(learner.uid, 'present')}
                            >
                              Present
                            </Button>
                            <Button
                              variant={attendanceDraft[learner.uid] === 'absent' ? 'danger' : 'outline-danger'}
                              size="sm"
                              onClick={() => handleStatusChange(learner.uid, 'absent')}
                            >
                              Absent
                            </Button>
                            <Button
                              variant={attendanceDraft[learner.uid] === 'excused' ? 'warning' : 'outline-warning'}
                              size="sm"
                              onClick={() => handleStatusChange(learner.uid, 'excused')}
                            >
                              Excused
                            </Button>
                            <Button
                              variant={attendanceDraft[learner.uid] === 'self_paced' ? 'info' : 'outline-info'}
                              size="sm"
                              onClick={() => handleStatusChange(learner.uid, 'self_paced')}
                            >
                              Self-paced
                            </Button>
                            <Button
                              variant={attendanceDraft[learner.uid] === 'other' ? 'primary' : 'outline-primary'}
                              size="sm"
                              onClick={() => handleStatusChange(learner.uid, 'other')}
                            >
                              Other
                            </Button>
                          </ButtonGroup>
                        </td>
                        {(() => {
                          const signature = getSignatureForLearner(learner);
                          const uploadKey = `${learner.uid}_${selectedDate}`;
                          const existingUpload = registerUploads[uploadKey];
                          return (
                            <>
                              <td>{signature ? 'Yes' : loadingSignatures ? 'Loading...' : 'No'}</td>
                              <td>{signature?.submittedAt ? new Date(signature.submittedAt).toLocaleString() : '-'}</td>
                              <td>
                                {signature?.pdfUrl ? (
                                  <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() => window.open(signature.pdfUrl, '_blank')}
                                  >
                                    Preview
                                  </Button>
                                ) : (
                                  '-'
                                )}
                              </td>
                              <td>
                                <div className="d-flex flex-column gap-1">
                                  {existingUpload?.fileUrl ? (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        onClick={() => window.open(existingUpload.fileUrl, '_blank')}
                                      >
                                        View
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline-danger"
                                        onClick={() => handleRemoveRegisterUpload(learner)}
                                        disabled={saving}
                                      >
                                        Remove
                                      </Button>
                                    </>
                                  ) : (
                                    <small className="text-muted">{loadingRegisterUploads ? 'Loading...' : 'No file'}</small>
                                  )}
                                  <Form.Control
                                    type="file"
                                    size="sm"
                                    accept=".pdf,.doc,.docx"
                                    onChange={(e) => handleRegisterUpload(learner, e.target.files?.[0])}
                                    disabled={saving}
                                  />
                                </div>
                              </td>
                            </>
                          );
                        })()}
                      </tr>
                    ))}
                  </tbody>
                </Table>
                </Card.Body>
              </Card>
            )}
          </>
        ) : (
          allowSessionCreateAndMark ? (
          <>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Learner</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Signed</th>
                  <th>Signed At</th>
                  <th>Register</th>
                  <th>Upload Register</th>
                </tr>
              </thead>
              <tbody>
                {learners.map((learner) => (
                  <tr key={learner.uid}>
                    <td>{learner.displayName || learner.uid}</td>
                    <td>{learner.email}</td>
                    <td>
                      <ButtonGroup>
                        <Button
                          variant={attendanceDraft[learner.uid] === 'present' ? 'success' : 'outline-success'}
                          size="sm"
                          onClick={() => handleStatusChange(learner.uid, 'present')}
                        >
                          Present
                        </Button>
                        <Button
                          variant={attendanceDraft[learner.uid] === 'absent' ? 'danger' : 'outline-danger'}
                          size="sm"
                          onClick={() => handleStatusChange(learner.uid, 'absent')}
                        >
                          Absent
                        </Button>
                        <Button
                          variant={attendanceDraft[learner.uid] === 'excused' ? 'warning' : 'outline-warning'}
                          size="sm"
                          onClick={() => handleStatusChange(learner.uid, 'excused')}
                        >
                          Excused
                        </Button>
                        <Button
                          variant={attendanceDraft[learner.uid] === 'self_paced' ? 'info' : 'outline-info'}
                          size="sm"
                          onClick={() => handleStatusChange(learner.uid, 'self_paced')}
                        >
                          Self-paced
                        </Button>
                        <Button
                          variant={attendanceDraft[learner.uid] === 'other' ? 'primary' : 'outline-primary'}
                          size="sm"
                          onClick={() => handleStatusChange(learner.uid, 'other')}
                        >
                          Other
                        </Button>
                      </ButtonGroup>
                    </td>
                    {(() => {
                      const signature = getSignatureForLearner(learner);
                      const uploadKey = `${learner.uid}_${selectedDate}`;
                      const existingUpload = registerUploads[uploadKey];
                      return (
                        <>
                          <td>{signature ? 'Yes' : loadingSignatures ? 'Loading...' : 'No'}</td>
                          <td>{signature?.submittedAt ? new Date(signature.submittedAt).toLocaleString() : '-'}</td>
                          <td>
                            {signature?.pdfUrl ? (
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => window.open(signature.pdfUrl, '_blank')}
                              >
                                Preview
                              </Button>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td>
                            <div className="d-flex flex-column gap-1">
                              {existingUpload?.fileUrl ? (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() => window.open(existingUpload.fileUrl, '_blank')}
                                  >
                                    View
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => handleRemoveRegisterUpload(learner)}
                                    disabled={saving}
                                  >
                                    Remove
                                  </Button>
                                </>
                              ) : (
                                <small className="text-muted">{loadingRegisterUploads ? 'Loading...' : 'No file'}</small>
                              )}
                              <Form.Control
                                type="file"
                                size="sm"
                                accept=".pdf,.doc,.docx"
                                onChange={(e) => handleRegisterUpload(learner, e.target.files?.[0])}
                                disabled={saving}
                              />
                            </div>
                          </td>
                        </>
                      );
                    })()}
                  </tr>
                ))}
              </tbody>
            </Table>

            <div className="d-flex justify-content-end gap-2">
              {lastMarkedSession && getCurrentPresentLearners().length > 0 && (
                <Button
                  variant="outline-primary"
                  onClick={handleSendRegisterLink}
                  disabled={saving}
                >
                  Send link to sign ({getCurrentPresentLearners().length})
                </Button>
              )}
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={saving || (!selectedProgram && !selectedCohort) || learners.length === 0}
              >
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  requestLearnerConfirmation ? 'Create Session & Notify Learners' : 'Save Attendance'
                )}
              </Button>
            </div>
          </>
          ) : (
            <Alert variant="info">
              Single-session attendance marking is restricted to internal staff. Use the "Override Attendance" section to confirm or correct attendance on an existing session.
            </Alert>
          )
        )}

        <hr />

        <h6 className="mb-2">Override Attendance</h6>
        <p className="text-muted mb-3">
          1) Select a cohort. 2) Pick the session you want to edit (historic sessions appear here too).
          3) Set status (Present/Absent/Excused/Self-paced/Other) and confirm overrides.
        </p>
        <Row className="mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Override Cohort Filter</Form.Label>
              <Form.Select
                value={overrideCohortFilter}
                onChange={(e) => setOverrideCohortFilter(e.target.value)}
                disabled={loadingOverrideSessions}
              >
                <option value="">Select a cohort...</option>
                <option value="all">All Assigned Cohorts</option>
                {cohorts.map((cohort) => (
                  <option key={cohort.id} value={cohort.id}>
                    {cohort.name || cohort.cohortName || cohort.id}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={8}>
            <Form.Group>
              <Form.Label className="d-flex align-items-center justify-content-between">
                <span>Select Session</span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setOverrideRefreshKey((prev) => prev + 1)}
                  disabled={loadingOverrideSessions}
                >
                  Refresh
                </Button>
              </Form.Label>
              <Form.Select
                value={overrideSessionId}
                onChange={(e) => setOverrideSessionId(e.target.value)}
                disabled={!overrideCohortFilter || loadingOverrideSessions}
              >
                <option value="">
                  {overrideCohortFilter ? 'Choose a session...' : 'Select a cohort first'}
                </option>
                {overrideSessions.map((session) => (
                  <option key={session.id} value={session.id}>
                    {(session.title || session.moduleName || 'Session')} •{' '}
                    {session.scheduledDate?.toLocaleDateString() || 'Unknown date'}
                  </option>
                ))}
              </Form.Select>
              {archiveError && (
                <div className="text-danger small mt-1">{archiveError}</div>
              )}
            </Form.Group>
          </Col>
          {!facilitatorMode && userProfile?.role === 'admin' && (
            <Col md={2} className="d-flex align-items-end">
              <Button
                variant="outline-danger"
                size="sm"
                disabled={!overrideSessionId || archivingSession}
                onClick={handleArchiveSession}
              >
                {archivingSession ? 'Archiving...' : 'Archive'}
              </Button>
            </Col>
          )}
        </Row>

        {overrideSessionId && (
          <AttendanceOverride
            sessionId={overrideSessionId}
            cohortId={overrideCohortFilter === 'all' ? null : overrideCohortFilter}
            onOverrideComplete={() => {
              setSuccess('Attendance override saved successfully.');
            }}
          />
        )}
      </Card.Body>

      <GoogleMeetAttendanceSync
        show={showTeamsSync}
        onHide={() => setShowTeamsSync(false)}
        onSync={(records) => {
          setTeamsSyncUsed(true);
          const mapped = {};
          const nextHistoryUpdates = {};
          let matchedCount = 0;
          let unmatchedCount = 0;

          records.forEach((record) => {
            const learner = resolveLearnerFromTeamsRecord(record);
            if (!learner?.uid) {
              unmatchedCount += 1;
              return;
            }

            matchedCount += 1;
            mapped[learner.uid] = record.status;
            const key = `${learner.uid}_${selectedDate}`;
            nextHistoryUpdates[key] = {
              ...(attendanceHistory[key] || {}),
              ...record,
              learnerId: learner.uid,
              learnerName: learner.displayName || learner.uid,
              email: learner.email || record.email || '',
              date: new Date(selectedDate)
            };
          });

          if (!matchedCount) {
            setError('No Teams attendees could be matched to learners in the selected cohort.');
            return;
          }

          Object.keys(mapped).forEach((learnerId) => {
            if (!mapped[learnerId]) {
              mapped[learnerId] = 'not_marked';
            }
          });

          setAttendanceHistory((prev) => ({
            ...prev,
            ...nextHistoryUpdates
          }));
          setAttendanceDraft((prev) => ({
            ...prev,
            ...mapped
          }));
          setShowTeamsSync(false);

          if (unmatchedCount > 0) {
            setSuccess(`Applied Teams attendance for ${matchedCount} learner(s). ${unmatchedCount} attendee row(s) could not be matched automatically.`);
          } else {
            setSuccess(`Applied Teams attendance for ${matchedCount} learner(s).`);
          }
        }}
        autoSyncEnabled={false}
      />

      {!facilitatorMode && (userProfile?.role === 'admin' || userProfile?.role === 'qa') && (
        <Card className="shadow-sm border-0 mt-4">
          <Card.Header className="bg-white">
            <h6 className="mb-0">Manage Historic Sessions</h6>
          </Card.Header>
          <Card.Body>
            <Row className="g-3 align-items-end">
              <Col md={5}>
                <Form.Group>
                  <Form.Label>Filter by Cohort</Form.Label>
                  <Form.Select
                    value={manageCohortFilter}
                    onChange={(e) => setManageCohortFilter(e.target.value)}
                  >
                    <option value="">All cohorts</option>
                    {cohorts.map((cohort) => (
                      <option key={cohort.id} value={cohort.id}>
                        {cohort.name || cohort.cohortName || cohort.id}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Check
                  type="switch"
                  id="show-archived-sessions"
                  label="Show archived"
                  checked={showArchivedSessions}
                  onChange={(e) => setShowArchivedSessions(e.target.checked)}
                />
              </Col>
            </Row>

            {managePasswordError && (
              <Alert variant="danger" className="mt-3">
                {managePasswordError}
              </Alert>
            )}

            {manageError && (
              <Alert variant="danger" className="mt-3">
                {manageError}
              </Alert>
            )}

            {manageLoading ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" />
              </div>
            ) : (
              <Table responsive size="sm" className="mt-3">
                <thead>
                  <tr>
                    <th>Session</th>
                    <th>Cohort</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Archived</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {manageSessions
                    .filter((session) => showArchivedSessions || !session.archived)
                    .map((session) => (
                      <tr key={session.id}>
                        <td>{session.title || session.moduleName || 'Session'}</td>
                        <td>{cohorts.find((c) => c.id === session.cohortId)?.name || session.cohortId || '—'}</td>
                        <td>{session.scheduledDate?.toLocaleDateString() || '—'}</td>
                        <td>{session.status || '—'}</td>
                        <td>{session.archived ? 'Yes' : 'No'}</td>
                        <td className="d-flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant={session.archived ? 'outline-secondary' : 'outline-warning'}
                            disabled={manageActionLoading}
                            onClick={() => handleArchiveToggle(session, !session.archived)}
                          >
                            {session.archived ? 'Restore' : 'Archive'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            disabled={manageActionLoading}
                            onClick={() => handleDeleteSession(session)}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      )}
    </Card>
  );
};

export default AttendanceRegister;
